import { useMemo, useState, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'
import { Bot, Globe, BookOpen, FileText, Copy, Download, Check } from 'lucide-react'

interface MessageAttachment {
  name: string
  type: string
  size: number
  previewUrl?: string
}

interface ChunkRef {
  chunkId: string
  docId: string
  docTitle: string
}

interface Props {
  role: 'user' | 'assistant' | 'system'
  content: string
  toolCalls?: any[]
  isStreaming?: boolean
  timestamp?: string
  attachments?: MessageAttachment[]
  document_refs?: string | ChunkRef[]
}

/** Map tool name to pill style */
function toolPillStyle(name: string) {
  switch (name) {
    case 'web_search':
      return { bg: 'bg-blue-50 text-blue-600', Icon: Globe, label: 'Web Search' }
    case 'knowledge_search':
      return { bg: 'bg-green-50 text-green-600', Icon: BookOpen, label: 'Knowledge Base' }
    default:
      return { bg: 'bg-gray-100 text-gray-600', Icon: null, label: name }
  }
}

/**
 * If content starts with **BLUF:** extract it as a separate block.
 * Returns { bluf, rest } where bluf may be null.
 */
function extractBluf(content: string): { bluf: string | null; rest: string } {
  const match = content.match(/^\*\*BLUF:\*\*\s*([\s\S]*?)(?=\n\n|##|$)/)
  if (!match) return { bluf: null, rest: content }

  const blufText = match[1]!.trim()
  const rest = content.slice(match[0].length).trim()
  return { bluf: blufText, rest }
}

export function ChatMessage({ role, content, toolCalls, isStreaming, timestamp, attachments, document_refs }: Props) {
  const [copied, setCopied] = useState(false)

  const { bluf, rest } = useMemo(
    () => (role === 'assistant' ? extractBluf(content) : { bluf: null, rest: content }),
    [content, role],
  )

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }, [content])

  const handleDownload = useCallback(() => {
    const blob = new Blob([content], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const date = new Date().toISOString().split('T')[0]
    a.download = `chat-response-${date}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [content])

  const sourceDocTitles = useMemo(() => {
    if (role !== 'assistant' || !document_refs) return []
    try {
      const refs: ChunkRef[] =
        typeof document_refs === 'string' ? JSON.parse(document_refs) : document_refs
      if (!Array.isArray(refs)) return []
      const seen = new Set<string>()
      return refs.reduce<string[]>((acc, ref) => {
        if (ref.docTitle && !seen.has(ref.docTitle)) {
          seen.add(ref.docTitle)
          acc.push(ref.docTitle)
        }
        return acc
      }, [])
    } catch {
      return []
    }
  }, [role, document_refs])

  /* ---------- User message ---------- */
  if (role === 'user') {
    return (
      <div className="flex justify-end">
        <div>
          <div className="max-w-[80%] ml-auto bg-primary-600 text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm leading-relaxed">
            {content}
          </div>
          {/* Attachments */}
          {attachments && attachments.length > 0 && (
            <div className="flex items-center gap-1.5 mt-1.5 ml-auto flex-wrap justify-end">
              {attachments.map((att, i) => (
                att.previewUrl ? (
                  <img
                    key={i}
                    src={att.previewUrl}
                    alt={att.name}
                    className="w-14 h-14 rounded-lg object-cover border border-gray-200"
                  />
                ) : (
                  <div
                    key={i}
                    className="flex items-center gap-1 bg-primary-500/20 text-white rounded px-1.5 py-0.5 text-[10px]"
                  >
                    <FileText className="w-3 h-3" />
                    <span className="truncate max-w-[80px]">{att.name}</span>
                  </div>
                )
              ))}
            </div>
          )}
          {timestamp && (
            <div className="text-[10px] text-gray-400 text-right mt-1 mr-1">
              {timestamp}
            </div>
          )}
        </div>
      </div>
    )
  }

  /* ---------- Assistant message ---------- */
  return (
    <>
      {/* Tool call pills */}
      {toolCalls && toolCalls.length > 0 && (
        <div className="flex items-center gap-2 ml-10">
          {toolCalls.map((tc: any, i: number) => {
            const { bg, Icon, label } = toolPillStyle(tc.name)
            return (
              <div
                key={i}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${bg}`}
              >
                {Icon && <Icon className="w-3 h-3" />}
                {label}
              </div>
            )
          })}
        </div>
      )}

      {/* Message body */}
      <div className="flex gap-3">
        {/* Avatar */}
        <div className="flex-shrink-0 w-7 h-7 bg-primary-50 rounded-lg flex items-center justify-center mt-0.5">
          <Bot className="w-4 h-4 text-primary-500" />
        </div>

        <div className="flex-1 min-w-0">
          {/* BLUF card */}
          {bluf && (
            <div
              className="bg-primary-50 rounded-r-lg px-3.5 py-2.5 mb-3"
              style={{ borderLeft: '3px solid #6d28d9' }}
            >
              <div className="text-[11px] font-semibold text-primary-700 uppercase tracking-wide mb-1">
                Bottom Line
              </div>
              <p className="text-sm text-gray-800 leading-relaxed">
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <>{children}</>,
                  }}
                >
                  {bluf}
                </ReactMarkdown>
              </p>
            </div>
          )}

          {/* Markdown content */}
          <div className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-li:text-gray-700 prose-code:text-primary-700 prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded">
            <ReactMarkdown rehypePlugins={[rehypeHighlight]}>
              {rest}
            </ReactMarkdown>
            {isStreaming && (
              <span className="inline-block w-0.5 h-4 bg-primary-500 animate-pulse ml-0.5" />
            )}
          </div>

          {/* Source citation badges */}
          {sourceDocTitles.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-gray-100">
              <span className="text-xs text-gray-500 mr-1">Sources:</span>
              {sourceDocTitles.map((title) => (
                <span
                  key={title}
                  className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full"
                >
                  {title}
                </span>
              ))}
            </div>
          )}

          {/* Action buttons - only show when not streaming */}
          {!isStreaming && content && (
            <div className="flex items-center gap-1 mt-2 pt-2 border-t border-gray-100">
              <button
                onClick={handleCopy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                title="Copy to clipboard"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-green-500" />
                    <span className="text-green-600">Copied</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    <span>Copy</span>
                  </>
                )}
              </button>
              <button
                onClick={handleDownload}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                title="Download as markdown"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Download</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
